import React from 'react';
import { Card, Button, Badge, Text, Heading } from './ui/index.jsx';

export default function InsufficientCreditsAlert({
    error,
    onClose,
    className = ''
}) {
    if (!error || error.type !== 'insufficient_credits') {
        return null;
    }

    const formatCredits = (credits) => {
        // Display credits as whole numbers for user-friendly display
        return Math.round(credits).toLocaleString();
    };
    
    // Calculate architectures for better user understanding
    const architecturesRequired = Math.ceil((error.required || 8) / 8);
    const architecturesAvailable = Math.floor((error.available || 0) / 8);
    const architecturesShort = Math.ceil((error.shortfall || 8) / 8);
    
    return (
        <Card className={`p-6 border-red-500/30 bg-red-500/5 ${className}`}>
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                    <span className="text-2xl mr-3">🚨</span>
                    <Heading level={3} size="sm" className="text-red-400">
                        Insufficient Credits
                    </Heading>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                )}
            </div>
            
            <div className="space-y-4">
                <Text variant="secondary">
                    {error.userMessage || 'You don\'t have enough credits to complete this operation.'}
                </Text>
                
                <div className="grid grid-cols-3 gap-4 p-4 bg-black/20 rounded-lg">
                    <div className="text-center">
                        <Text variant="muted" size="xs" className="mb-1">Required</Text>
                        <Badge variant="error" size="lg">
                            {formatCredits(error.required)}
                        </Badge>
                        <Text variant="muted" size="xs" className="mt-1">
                            ({architecturesRequired} arch.)
                        </Text>
                    </div>
                    <div className="text-center">
                        <Text variant="muted" size="xs" className="mb-1">Available</Text>
                        <Badge variant="warning" size="lg">
                            {formatCredits(error.available)}
                        </Badge>
                        <Text variant="muted" size="xs" className="mt-1">
                            ({architecturesAvailable} arch.)
                        </Text>
                    </div>
                    <div className="text-center">
                        <Text variant="muted" size="xs" className="mb-1">Shortfall</Text>
                        <Badge variant="error" size="lg">
                            {formatCredits(error.shortfall)}
                        </Badge>
                        <Text variant="muted" size="xs" className="mt-1">
                            ({architecturesShort} arch.)
                        </Text>
                    </div>
                </div>
                
                {error.breakdown && (
                    <div className="p-3 bg-black/10 rounded-lg">
                        <Text variant="muted" size="xs" className="mb-2">Cost Breakdown:</Text>
                        <div className="space-y-1">
                            {Object.entries(error.breakdown).map(([step, cost]) => (
                                <div key={step} className="flex justify-between text-xs">
                                    <Text variant="secondary" size="xs">
                                        {step.charAt(0).toUpperCase() + step.slice(1)}:
                                    </Text>
                                    <Text variant="secondary" size="xs">
                                        {formatCredits(cost)} credits
                                    </Text>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {onClose && (
                    <div className="flex gap-3 pt-2">
                        <Button
                            onClick={onClose}
                            variant="outline"
                            size="md"
                            className="flex-1"
                        >
                            Close
                        </Button>
                    </div>
                )}
            </div>
        </Card>
    );
}